import { useState } from 'react'

import {
  MissingAgreementDataDocument,
  MissingAgreementDataQueryVariables,
} from 'generated/resources/graphql'
import { uniqBy } from 'lodash'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'

import { MissingGrossSalaryData } from '../types/data'

const ITEMS_PER_PAGE = 10

export interface ReturnedData {
  data: MissingGrossSalaryData[]
  loading: boolean
  hasMore: boolean
  setLastCursor: (cursor: string | null) => void
  lastCursor?: string | null
  endCursor: string | null
  pageInfo?: string | null
  totalCount: number
}

export const useMissingAgreementData = ({
  variables,
}: {
  variables: MissingAgreementDataQueryVariables
}): ReturnedData => {
  const [lastCursor, setLastCursor] = useState<string | undefined | null>()
  const [items, setItems] = useState<MissingGrossSalaryData[]>([])

  const queryVariables = {
    ...variables,
    period: 'monthly',
    itemsPerPage: ITEMS_PER_PAGE,
    after: lastCursor,
  }

  const { data, loading } = useQuery(MissingAgreementDataDocument, {
    variables: {
      ...queryVariables,
      period: 'monthly',
    },
  })

  const records =
    data?.financialWorkspace?.employeesIncompleteContractsConnection.edges.map(
      (edge) => edge.node
    ) ?? []

  useUnsafeEffect(() => {
    if (data) {
      const newUniqueItems = records.filter((e) => !items.find((i) => i.id === e.id))

      if (newUniqueItems.length)
        setItems((prev) => {
          return uniqBy([...prev, ...newUniqueItems], 'id')
        })
    }
  }, [records])

  return {
    data: items,
    loading,
    hasMore:
      data?.financialWorkspace?.employeesIncompleteContractsConnection?.pageInfo?.hasNextPage ??
      false,
    setLastCursor,
    lastCursor,
    endCursor:
      data?.financialWorkspace?.employeesIncompleteContractsConnection?.pageInfo?.endCursor ?? '',
    totalCount: data?.financialWorkspace?.employeesIncompleteContractsConnection?.totalCount ?? 0,
  }
}
